import { config } from '@/forms/config';
import { ValidationAdapter } from '@/forms/core';
import type {
  FormConditionalValidation,
  FormField,
  RequiredWhenValueRestriction,
} from '@/forms/typings';
import { formFieldHasFormikValue } from './formFieldHasFormikValue';
import { generateConditionalValidation } from './generateConditionalValidation';
import { generateConditionalValidationFieldIds } from './generateConditionalValidationFieldIds';

/** The synthetic Formik key for a group's "required when value" rule. */
export const requiredWhenValueKey = (groupFieldId: string): string =>
  `${groupFieldId}${config.requiredWhenValueFieldSuffix}`;

/** Resolves a child by its seed ItemID. groupFieldId already carries the instance index. */
const findChildByItemID = (
  children: FormField[],
  groupFieldId: string,
  itemID: string,
): FormField | undefined =>
  children.find(child => child.fieldId === `${groupFieldId}_${itemID}`);

/**
 * Yup schema for a non-editable Formik key that validates "the target child holds
 * data when the trigger sibling equals the configured value". Gated by the group's
 * visibility conditions so hidden subtrees never block submit.
 */
export const createRequiredWhenValueYupSchema = (
  children: FormField[],
  restriction: RequiredWhenValueRestriction,
  groupFieldId: string,
  conditions: FormConditionalValidation[] | undefined,
) => {
  const Yup = ValidationAdapter.getLibrary();
  const message = restriction.message?.trim() || 'This field is required.';

  const base = Yup.mixed().nullable();

  const tested = base.test({
    name: 'requiredWhenValue',
    message,
    test() {
      const root = this.parent as Record<string, unknown> | undefined;
      if (!root) return true;

      // Mirrors the server: an unconfigured trigger value requires nothing.
      const expected = restriction.whenBoolEquals;
      if (expected !== true && expected !== false) return true;

      const trigger = findChildByItemID(
        children,
        groupFieldId,
        restriction.whenItemID,
      );

      // Unresolvable trigger fails open, so the server can never reject a form
      // the client accepted.
      if (!trigger) return true;

      // An untouched checkbox has no Formik value; unchecked reads as false.
      if ((root[trigger.fieldId] === true) !== expected) return true;

      const target = findChildByItemID(
        children,
        groupFieldId,
        restriction.requiredFieldItemID,
      );

      // A misconfigured seed must never make the client stricter than the server.
      if (!target) return true;

      // Server's IsEmpty() uses string.IsNullOrWhiteSpace: an untrimmed check
      // here would let a whitespace-only entry through to a BUS_002 on submit.
      const targetValue = root[target.fieldId];
      if (typeof targetValue === 'string' && targetValue.trim() === '') return false;

      return formFieldHasFormikValue(target, root);
    },
  });

  if (!conditions?.length) return tested;

  return base.when(
    generateConditionalValidationFieldIds(conditions),
    (values, schema) =>
      generateConditionalValidation(conditions, values) ? tested : schema,
  );
};
